import type {
  EQuestionType,
  TAvailableLanguageCodes,
  TFormSubType,
  TIntent,
  TLocalized,
  TMultipleSelectOption,
  TQuestionConversationSettings,
  TQuestionDisplayMode,
  TQuestionPresentation,
  TRankingOption,
  TReusableOption,
  TObjectives,
  TSingleSelectOption,
  TSlotFilling,
} from '@hubertai/screening-question-types';
import { useContext } from 'react';
import type { ReactNode } from 'react';
import type { QuestionEditorContextValue } from './QuestionEditorContext';
import { QuestionEditorContext } from './QuestionEditorContext';
import type { TIsDefaultValue } from 'types/questions/question.types.v4';
import type { TQuestionRule } from 'types/rules.types';
import type { TSchedulingConfig } from 'types/questions/scheduling.types';

export enum ECreateQuestionMode {
  Create = 'create',
  Edit = 'edit',
  Copy = 'copy',
  InTemplateCreate = 'in-template-create',
  InTemplateEdit = 'in-template-edit',
  InTemplateCopy = 'in-template-copy',
}

export const CREATE_MODES: ECreateQuestionMode[] = [
  ECreateQuestionMode.Create,
  ECreateQuestionMode.Copy,
  ECreateQuestionMode.InTemplateCreate,
  ECreateQuestionMode.InTemplateCopy,
];

export type TCreateQuestion = {
  _id?: string;
  id: string;
  copiedIdMap?: Record<string, string>;

  version: number;
  type: EQuestionType;
  short_description: string;
  language: TAvailableLanguageCodes;
  availableLanguages?: TAvailableLanguageCodes[];
  source_question_id?: string;

  // Scope
  client_id?: string;
  visibility: 'global' | 'client' | 'local' | null;

  pre_actions: string[];
  post_actions: string[];

  intro: TIsDefaultValue;
  text: TIsDefaultValue;
  outro: TIsDefaultValue;
  clarify: TIsDefaultValue;

  plain_text: TLocalized<string>;
  help_text?: TLocalized<string>;

  // Ranking question
  randomize_order?: boolean;
  title?: TLocalized<string>;
  min_required?: number;
  ranking_mode?: 'dnd' | 'click';

  // Audio / video (recording) question
  max_duration_seconds?: number;
  min_duration_seconds?: number;
  allow_retake?: boolean;
  max_retakes?: number;
  max_thinking_seconds?: number;
  recording_quality?: '360p' | '480p' | '720p';
  /** When true the candidate must record (cannot opt out); defaults to optional. */
  required?: boolean;

  // Scheduling (cal.com) question
  scheduling?: TSchedulingConfig;
  /** Scheduling: booking-confirmed reply line(s) per language (question-level TLocalized<string[]>). */
  replies?: Partial<Record<string, string[]>>;

  // Forms
  forms: TFormSubType[];
  // Options
  options: (TSingleSelectOption | TMultipleSelectOption | TReusableOption | TRankingOption)[];
  min_selections?: number;
  max_selections?: number;
  // Single select
  display_mode?: TQuestionDisplayMode;
  // Open
  scoring_category?: string;
  weight?: number;
  intents: TIntent[];
  slot_filling?: TSlotFilling;
  objectives?: TObjectives;
  // Summary presentation
  presentation: TQuestionPresentation;
  access_groups_ids?: string[];
  conversation_settings?: TQuestionConversationSettings;
  send_data_to_looker?: boolean;
  deferrable?: boolean;
  // Read-only: assigned by the backend, never sent back on save.
  classification_names?: { type: string; topics: string[] };
  rules?: TQuestionRule[];
};
export const QuestionEditorProvider = ({ questionId, children }: { questionId: string; children: ReactNode }) => {
  return <QuestionEditorContext.Provider value={{ questionId }}>{children}</QuestionEditorContext.Provider>;
};
export const useQuestionEditorContext = (): QuestionEditorContextValue => {
  const x = useContext(QuestionEditorContext);
  if (!x) {
    throw new Error('QuestionEditorContext must be used within a QuestionEditorProvider');
  }
  return x;
};
